import { Images, Paperclip, Upload } from "lucide-react"
import { useRef, useState, type RefObject } from "react"
import { toast } from "sonner"
import { LibraryPickerDialog, type LibraryProjectChoice } from "@/components/library/library-picker-dialog"
import { PEEK_COMPACT_MENU_MIN_WIDTH, PeekAnchoredMenu, PeekMenuItem } from "@/components/platform/company-peek-parts"
import type { MentionItem } from "@/components/prompt-editor/types"
import type { TaskEditorFileInserter } from "@/components/tasks/task-description-editor"
import type { LibraryPickResult } from "@/hooks/use-library-pick-selection"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"
import type { ShotFrameSource } from "@/lib/workspace-api"

const LIBRARY_TARGET_ID = "task"

// Выбранное в Библиотеке прикрепляется по одному, неудачи собираются в одно уведомление; true — всё прикреплено.
export async function attachLibraryPicks(
  picked: readonly LibraryPickResult[],
  attachOne: (source: ShotFrameSource) => Promise<void>,
  failedMessage: (names: string) => string,
): Promise<boolean> {
  const failed: string[] = []
  for (const entry of picked) {
    try {
      await attachOne(entry.source)
    } catch (error) {
      const reason = error instanceof Error ? error.message.trim() : ""
      failed.push(reason ? `${entry.label} — ${reason}` : entry.label)
    }
  }
  if (failed.length > 0) toast.error(failedMessage(failed.join(", ")))
  return failed.length === 0
}

// Окно Библиотеки для файлов задачи: у блока «Файлы» и у скрепки комментария.
export function TaskLibraryPicker({
  open,
  onOpenChange,
  projectId,
  frameFormat,
  pickedKeys,
  busy = false,
  projectChoices,
  projectChoicesLoading = false,
  onProjectChange,
  onPick,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number | null
  frameFormat: string
  pickedKeys?: readonly string[]
  busy?: boolean
  projectChoices?: readonly LibraryProjectChoice[]
  projectChoicesLoading?: boolean
  onProjectChange?: (projectId: number) => void
  onPick: (picked: readonly LibraryPickResult[]) => void
}) {
  const { t } = useTranslation("tasks")
  return (
    <LibraryPickerDialog
      open={open}
      onOpenChange={onOpenChange}
      projectId={projectId}
      frameFormat={frameFormat}
      pickedKeys={pickedKeys}
      projectChoices={projectChoices}
      projectChoicesLoading={projectChoicesLoading}
      onProjectChange={onProjectChange}
      subtitle={t("files.fromLibrary")}
      targets={[{ id: LIBRARY_TARGET_ID, label: t("files.attach"), accepts: ["image", "video", "audio"] }]}
      busyTargetId={busy ? LIBRARY_TARGET_ID : null}
      multiple
      onPick={() => undefined}
      onPickMany={(_targetId, picked) => {
        if (picked.length > 0) onPick(picked)
      }}
    />
  )
}

export type TaskCommentAttach = {
  projectId: number
  frameFormat: string
  onUpload: (files: File[]) => Promise<MentionItem[]>
  onLibraryPick: (picked: readonly LibraryPickResult[]) => Promise<MentionItem[]>
  onLibraryOpenChange: (open: boolean) => void
}

// Скрепка комментария: файл прикрепляется к задаче, а ссылка на него встаёт на место курсора, как при вставке.
export function TaskCommentAttachMenu({
  attach,
  insertFilesRef,
  disabled,
}: {
  attach: TaskCommentAttach
  insertFilesRef: RefObject<TaskEditorFileInserter | null>
  disabled: boolean
}) {
  const { t } = useTranslation("tasks")
  const inputRef = useRef<HTMLInputElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)

  const changeLibraryOpen = (open: boolean) => {
    setLibraryOpen(open)
    attach.onLibraryOpenChange(open)
  }

  const insert = (request: Promise<MentionItem[]>) => {
    insertFilesRef.current?.(request)
  }

  return (
    <>
      <PeekAnchoredMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        menuMinWidth={PEEK_COMPACT_MENU_MIN_WIDTH}
        trigger={(ref) => (
          <button
            ref={ref}
            type="button"
            aria-label={t("comments.attach")}
            title={t("comments.attach")}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            disabled={disabled}
            onClick={() => setMenuOpen((prev) => !prev)}
            className={cn(
              "flex size-7 items-center justify-center rounded-lg text-ui-text-muted transition-colors hover:bg-ui-surface-raised hover:text-ui-text-primary disabled:pointer-events-none disabled:opacity-40",
              menuOpen && "bg-ui-surface-raised text-ui-text-primary",
            )}
          >
            <Paperclip className="size-3.5" />
          </button>
        )}
      >
        <PeekMenuItem
          icon={<Upload />}
          label={t("files.fromComputer")}
          onSelect={() => {
            setMenuOpen(false)
            inputRef.current?.click()
          }}
        />
        <PeekMenuItem
          icon={<Images />}
          label={t("files.fromLibrary")}
          onSelect={() => {
            setMenuOpen(false)
            changeLibraryOpen(true)
          }}
        />
      </PeekAnchoredMenu>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          event.target.value = ""
          if (files.length > 0) insert(attach.onUpload(files))
        }}
      />
      <TaskLibraryPicker
        open={libraryOpen}
        onOpenChange={changeLibraryOpen}
        projectId={attach.projectId}
        frameFormat={attach.frameFormat}
        onPick={(picked) => {
          insert(attach.onLibraryPick(picked))
          changeLibraryOpen(false)
        }}
      />
    </>
  )
}
